/*
 * OrbitDelegated — the recipient-facing scoped collector.
 *
 * Someone (a founder / owner) shared one or more cards from their Orbit
 * scope with a teammate (CTO, CFO, sponsor…). The teammate opens the
 * magic link → lands HERE. No account, no auth — the token in the URL
 * is the boundary (validated server-side).
 *
 * This is Orbit in "delegated collection mode": Orbit greets, then
 * walks ONLY the shared fields one at a time (its voice + the same
 * pane/chip/text surface the main intake uses), auto-advancing on each
 * answer. On completion it thanks them and tells them the founder's
 * been notified — and the backend fires a real email to the owner.
 *
 * It deliberately does NOT run the full intake agent loop (no routing,
 * no suggestions, no PRD offers) — those are wrong for an
 * unauthenticated recipient filling 2 fields. Just clean collection.
 *
 * Reuses orbit-dossier.css pane + chip styling for visual continuity
 * with the main Orbit surface.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowRight, Check, Sun, Moon } from 'lucide-react';

import { useOrbitTheme } from './useOrbitTheme';
import {
  getDelegated,
  submitDelegated,
  type DelegatedPayload,
  type DelegatedField,
} from './lib/intakeApi';

import './styles/orbit-tokens.css';
import './styles/orbit-intake.css';
import './styles/orbit-dossier.css';


type LoadState = 'loading' | 'ready' | 'invalid' | 'done';


export function OrbitDelegated() {
  const { token } = useParams<{ token: string }>();
  const { theme, toggle: toggleTheme } = useOrbitTheme();

  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [payload, setPayload] = useState<DelegatedPayload | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [respondentName, setRespondentName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [textDraft, setTextDraft] = useState('');

  // ── Load the token ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    if (!token) { setLoadState('invalid'); return; }
    (async () => {
      try {
        const data = await getDelegated(token);
        if (cancelled) return;
        if (!data.ok || !data.fields || data.fields.length === 0) {
          setLoadState('invalid');
          return;
        }
        // Seed answers with any existing prefill values.
        const seed: Record<string, unknown> = {};
        for (const f of data.fields) {
          if (f.value !== undefined && f.value !== null && f.value !== '') {
            seed[f.key] = f.value;
          }
        }
        setPayload(data);
        setAnswers(seed);
        setLoadState(data.completed_at ? 'done' : 'ready');
      } catch (e) {
        if (!cancelled) setLoadState('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const fields = payload?.fields ?? [];
  const activeField: DelegatedField | undefined = fields[activeIdx];
  const isLastField = activeIdx >= fields.length - 1;

  // Reset the text draft whenever the active field changes (prefill it
  // with an existing value if present).
  useEffect(() => {
    if (!activeField) return;
    const existing = answers[activeField.key];
    setTextDraft(typeof existing === 'string' ? existing : '');
  }, [activeIdx, activeField]); // eslint-disable-line react-hooks/exhaustive-deps

  const ownerLabel = useMemo(() => {
    const c = payload?.company_name;
    return c ? c : 'the team';
  }, [payload]);

  // Who asked for this. Prefer the delegator's name (the person who sent the
  // link); fall back to the company, then a neutral label.
  const requesterLabel = useMemo(() => {
    return payload?.delegated_by || payload?.company_name || 'the team';
  }, [payload]);

  // ── Submit all answers ────────────────────────────────────────────
  const finish = useCallback(async (finalAnswers: Record<string, unknown>) => {
    if (!token || submitting) return;
    setSubmitting(true);
    try {
      const res = await submitDelegated(token, finalAnswers, respondentName.trim() || undefined);
      setSavedCount(res.count);
      setLoadState('done');
    } catch (e) {
      console.error('[delegated] submit failed', e);
      // Surface a soft error but stay on the form so they can retry.
      setSubmitting(false);
    }
  }, [token, submitting, respondentName]);

  // ── Advance helper ────────────────────────────────────────────────
  const advance = useCallback((updated: Record<string, unknown>) => {
    if (isLastField) {
      void finish(updated);
    } else {
      setActiveIdx((i) => Math.min(fields.length - 1, i + 1));
    }
  }, [isLastField, finish, fields.length]);

  // ── Answer handlers ───────────────────────────────────────────────
  const handleChipPick = useCallback((field: DelegatedField, value: string) => {
    if (field.kind === 'multiselect') {
      // toggle; do NOT auto-advance (multi-pick needs a Next button)
      setAnswers((prev) => {
        const arr = Array.isArray(prev[field.key]) ? (prev[field.key] as string[]) : [];
        const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
        return { ...prev, [field.key]: next };
      });
      return;
    }
    setAnswers((prev) => {
      const updated = { ...prev, [field.key]: value };
      // brief beat so the user sees the chip light up before advancing
      window.setTimeout(() => advance(updated), 240);
      return updated;
    });
  }, [advance]);

  const handleMultiNext = useCallback((field: DelegatedField) => {
    setAnswers((prev) => {
      advance(prev);
      return prev;
    });
  }, [advance]);

  const handleTextSubmit = useCallback((field: DelegatedField) => {
    const v = textDraft.trim();
    setAnswers((prev) => {
      const updated = v ? { ...prev, [field.key]: v } : prev;
      advance(updated);
      return updated;
    });
  }, [textDraft, advance]);

  const handleSkip = useCallback((field: DelegatedField) => {
    setAnswers((prev) => {
      const updated = { ...prev };
      delete updated[field.key]; // skipped → leave the field untouched server-side
      advance(updated);
      return updated;
    });
  }, [advance]);

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="orbit-intake-root orbit-intake-root--chat" data-theme={theme}>
      <header className="aa-topbar">
        <div className="aa-brand">
          <div className="aa-brand-mark" aria-hidden="true">
            <img src="/orbit/logos/aa-logo-icon-white.svg" alt="" width={18} height={18} />
          </div>
          <span className="aa-brand-name">Orbit</span>
        </div>
        <button
          type="button"
          className="aa-theme-toggle"
          onClick={toggleTheme}
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        >
          {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
        </button>
      </header>

      <main className="aa-chat-shell aa-chat-shell--pane aa-delegated">
        <section className="aa-pane-stage">
          {loadState === 'loading' && (
            <div className="aa-delegated-state">
              <span className="aa-pane-thinking-dot" />
              <span className="aa-pane-thinking-dot" />
              <span className="aa-pane-thinking-dot" />
            </div>
          )}

          {loadState === 'invalid' && (
            <div className="aa-turn-pane aa-turn-pane--latest">
              <header className="aa-turn-pane-meta">
                <span className="aa-turn-pane-section">Link</span>
              </header>
              <div className="aa-turn-pane-body">
                <p className="aa-delegated-q">This link isn't valid anymore.</p>
                <p className="aa-delegated-why">
                  It may have expired or been completed already. Ask whoever
                  shared it to send a fresh one.
                </p>
              </div>
            </div>
          )}

          {loadState === 'done' && (
            <div className="aa-turn-pane aa-turn-pane--latest">
              <div className="aa-turn-pane-body">
                <div className="aa-delegated-done">
                  <span className="aa-delegated-done-check">
                    <Check size={18} strokeWidth={3} />
                  </span>
                  <p className="aa-delegated-q">That's everything — thank you.</p>
                  <p className="aa-delegated-why">
                    {savedCount > 0
                      ? `I've saved your ${savedCount} answer${savedCount !== 1 ? 's' : ''} and let ${requesterLabel} know.`
                      : `I've let ${requesterLabel} know you're done.`}
                    {' '}You can close this tab.
                  </p>
                </div>
              </div>
            </div>
          )}

          {loadState === 'ready' && activeField && (
            <div className="aa-turn-pane aa-turn-pane--latest" key={activeIdx}>
              <header className="aa-turn-pane-meta">
                <span className="aa-turn-pane-section">
                  {payload?.section_label}
                  {payload?.role_label ? ` · for the ${payload.role_label}` : ''}
                </span>
                <span className="aa-turn-pane-counter">
                  {String(activeIdx + 1).padStart(2, '0')}
                  <span className="aa-turn-pane-counter-sep">/</span>
                  {String(fields.length).padStart(2, '0')}
                </span>
              </header>

              <div className="aa-turn-pane-body">
                {/* Orbit greeting only on the first field */}
                {activeIdx === 0 && (
                  <p className="aa-delegated-greeting">
                    {requesterLabel} asked me to collect a few things from you.
                    {' '}Quick — just {fields.length} question{fields.length !== 1 ? 's' : ''}.
                  </p>
                )}

                <p className="aa-delegated-q">{activeField.label}</p>
                {activeField.why && (
                  <p className="aa-delegated-why">{activeField.why}</p>
                )}

                {/* Input: chips for select/multiselect, text otherwise */}
                {(activeField.kind === 'select' || activeField.kind === 'multiselect'
                  || activeField.kind === 'chip') && (activeField.options?.length ?? 0) > 0 ? (
                  <>
                    <div className="aa-delegated-chips">
                      {(activeField.options ?? []).map((opt) => {
                        const cur = answers[activeField.key];
                        const picked = activeField.kind === 'multiselect'
                          ? Array.isArray(cur) && (cur as string[]).includes(opt.value)
                          : cur === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className={'aa-compound-chips-chip' + (picked ? ' aa-compound-chips-chip--on' : '')}
                            onClick={() => handleChipPick(activeField, opt.value)}
                            disabled={submitting}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    {activeField.kind === 'multiselect'
                      && Array.isArray(answers[activeField.key])
                      && (answers[activeField.key] as string[]).length > 0 && (
                      <div className="aa-delegated-actions">
                        <button
                          type="button"
                          className="aa-delegated-next"
                          onClick={() => handleMultiNext(activeField)}
                          disabled={submitting}
                        >
                          <span>{isLastField ? 'Finish' : 'Next'}</span>
                          <ArrowRight size={13} strokeWidth={2.5} />
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="aa-delegated-text">
                    <textarea
                      className="aa-delegated-textarea"
                      value={textDraft}
                      onChange={(e) => setTextDraft(e.target.value)}
                      placeholder={activeField.placeholder || 'Type your answer…'}
                      rows={activeField.kind === 'longtext' ? 4 : 2}
                      disabled={submitting}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          handleTextSubmit(activeField);
                        }
                      }}
                      autoFocus
                    />
                    <div className="aa-delegated-actions">
                      <button
                        type="button"
                        className="aa-delegated-next"
                        onClick={() => handleTextSubmit(activeField)}
                        disabled={submitting || !textDraft.trim()}
                      >
                        <span>{isLastField ? 'Finish' : 'Next'}</span>
                        <ArrowRight size={13} strokeWidth={2.5} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Skip — optional, advances without writing */}
                <button
                  type="button"
                  className="aa-delegated-skip"
                  onClick={() => handleSkip(activeField)}
                  disabled={submitting}
                >
                  Skip this one
                </button>

                {/* Optional name on the last field so the founder's
                    email can say who filled it. */}
                {isLastField && (
                  <div className="aa-delegated-name">
                    <label className="aa-delegated-name-label" htmlFor="aa-resp-name">
                      Your name (optional)
                    </label>
                    <input
                      id="aa-resp-name"
                      type="text"
                      className="aa-delegated-name-input"
                      value={respondentName}
                      onChange={(e) => setRespondentName(e.target.value)}
                      placeholder="So they know who to thank"
                      disabled={submitting}
                    />
                  </div>
                )}

                {submitting && (
                  <p className="aa-compound-chips-saving">Saving…</p>
                )}
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default OrbitDelegated;
